import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Sparkle, ArrowUp, Clock } from "@phosphor-icons/react";

const PLAN_STYLE = {
  free_trial: "bg-white/10 text-white/70",
  clarolight_pro: "bg-indigo-500/20 text-indigo-200",
  shield_go2: "bg-emerald-500/20 text-emerald-200",
  shield_gop2: "bg-emerald-500/20 text-emerald-200",
  shield_clo: "bg-emerald-500/20 text-emerald-200",
  shield_clop: "bg-emerald-500/20 text-emerald-200",
  shield_full: "bg-amber-500/20 text-amber-200",
};

// Live plan badge + AI-credit balance for the sidebar. Refreshes on mount, on
// entity switch, on window focus, and when a purchase/consume dispatches
// `claro:tenant-updated`.
export const CreditMeter = () => {
  const [t, setT] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/me");
      setT(data);
    } catch (e) { /* silent — non-blocking chrome */ }
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("claro:tenant-updated", h);
    window.addEventListener("claro:registrations-changed", h);
    window.addEventListener("focus", h);
    return () => {
      window.removeEventListener("claro:tenant-updated", h);
      window.removeEventListener("claro:registrations-changed", h);
      window.removeEventListener("focus", h);
    };
  }, [load]);

  if (!t) return null;
  const total = t?.credits?.total ?? 0;
  const base = t?.credits?.base ?? 0;
  const topup = t?.credits?.topup ?? 0;
  const allot = t?.credits?.monthly_allotment ?? 0;
  const resetAt = t?.credits?.reset_at;
  const resetStr = resetAt ? new Date(resetAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
  const isLight = ["free_trial", "clarolight_pro"].includes(t.plan);
  const lowCredits = allot > 0 && base < allot * 0.10;
  const pastDue = t.plan_status === "past_due";
  const trialEnds = t.plan === "free_trial" && t.trial_ends_at ? new Date(t.trial_ends_at) : null;
  const trialDaysLeft = trialEnds ? Math.max(0, Math.ceil((trialEnds - new Date()) / 86400000)) : null;
  const trialEnded = t.plan === "free_trial" && t.locked;
  const trialUrgent = trialEnded || (trialDaysLeft != null && trialDaysLeft <= 3);
  const meterTitle = `Monthly: ${base.toLocaleString()}${resetStr ? ` (resets ${resetStr})` : ""} · Top-up: ${topup.toLocaleString()}`;

  return (
    <div data-testid="credit-meter" className={`mb-3 rounded-lg border px-3 py-2.5 ${lowCredits || pastDue ? "bg-red-500/10 border-red-500/30" : "bg-white/5 border-white/10"}`}>
      <div className="flex items-center justify-between">
        <span
          data-testid="credit-meter-plan"
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${PLAN_STYLE[t.plan] || "bg-white/10 text-white/70"}`}
          title={t.locked ? "Account access paused" : t.plan_label}
        >
          {t.plan_label}{trialEnded ? " · ended" : t.locked ? " · paused" : pastDue ? " · past due" : ""}
        </span>
        <span data-testid="credit-meter-credits" className={`flex items-center gap-1 text-xs tabular-nums ${lowCredits ? "text-red-300 font-semibold" : "text-white/80"}`} title={meterTitle}>
          <Sparkle size={12} weight="fill" className={lowCredits ? "text-red-400" : "text-amber-300"} />
          {total.toLocaleString()}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between">
        <span className={`text-[10px] ${trialEnded ? "text-red-300" : lowCredits ? "text-red-300" : "text-white/40"}`} title={meterTitle} data-testid="credit-meter-status">
          {trialEnded ? "Trial ended — read-only" : lowCredits ? "Low — add credits" : resetStr ? `Resets ${resetStr}` : "AI credits"}
        </span>
        {(isLight || lowCredits || pastDue) && (
          <Link
            to="/billing"
            data-testid="credit-meter-upgrade"
            className={`flex items-center gap-0.5 text-[10px] font-semibold ${lowCredits || pastDue ? "text-red-300 hover:text-red-200" : "text-sky-300 hover:text-sky-200"}`}
          >
            <ArrowUp size={10} weight="bold" /> {pastDue ? "Fix billing" : lowCredits ? "Add credits" : "Upgrade"}
          </Link>
        )}
      </div>
      {t.plan === "free_trial" && (
        <div data-testid="trial-countdown"
          className={`mt-2 flex items-center justify-between gap-2 border-t pt-1.5 text-[10px] ${trialUrgent ? "border-red-500/30 text-red-300" : "border-white/10 text-amber-200"}`}>
          <span className="flex items-center gap-1" title="Free trial period">
            <Clock size={11} weight="fill" />
            {trialEnded ? <b>Trial ended</b> : trialDaysLeft != null ? <><b>{trialDaysLeft}</b> day{trialDaysLeft === 1 ? "" : "s"} left in trial</> : "Free trial"}
          </span>
          <Link to="/billing" data-testid="trial-upgrade" className="flex items-center gap-0.5 font-semibold hover:underline">
            <ArrowUp size={10} weight="bold" /> Upgrade
          </Link>
        </div>
      )}
    </div>
  );
};

export default CreditMeter;
